import React, { FC, memo, useCallback, useContext, useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import styled from 'styled-components';
import { produce } from 'immer';

import AudioPlayer from 'src/presentation/components/forms/AudioPlayer';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import FormContext from 'src/presentation/contexts/FormContext';
import RecordingLibrary from 'src/presentation/components/pages/shared/RecordingLibrary';
import VoiceSelector from './VoiceSelector';
import analytics from '@ad-scripts/lib/analytics';
import { MESSAGE_TYPES_OPTIONS } from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import { MessageType } from 'src/models/Ivrs/Nodes/message';
import { MultiContentRow, Row } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import { getRecordingFileName } from 'src/presentation/components/pages/shared/utils/Utils';
import TextareaField from 'src/presentation/components/forms/TextareaField';

type MessageTypeSelectorType = {
  attr: string;
  disabled?: boolean;
  readOnly?: boolean;
  value?: string;
};

type AudioSectionType = {
  messageAttr: string;
  readOnly?: boolean;
  url?: string;
};

const FILENAME_WITH_TEMPLATE_REGEX = /^(?<prefix>.+)?\{\{/;

const MessageTypeSelector: FC<MessageTypeSelectorType> = ({ attr, disabled, readOnly }) => {
  const { onBlur, workingState } = useContext(FormContext);

  const messageTypeAttr = `${attr}.message.type`;
  const messageAttr = `${attr}.message.message`;

  const messageType = get(workingState, messageTypeAttr);
  const currentMessage = get(workingState, messageAttr, '');

  const isRecordingMessage = messageType === MessageType.RECORDING;
  const isTextMessage = messageType === MessageType.TEXT;

  const [messages, setMessages] = useState({
    [MessageType.RECORDING]: '',
    [MessageType.TEXT]: '',
    [messageType]: currentMessage,
  });

  const useOnMessageTypeChange = value => {
    // saves message of the previous selected type in the local state to prevent overriding messages of different types
    setMessages({ ...messages, [messageType]: currentMessage });

    const newMessage = {
      message: messages[value],
      type: value,
    };

    const newWorkingState = cloneDeep(workingState);
    set(newWorkingState, `${attr}.message`, newMessage);

    onBlur &&
      onBlur({
        currentState: newWorkingState,
      });
  };

  return (
    <>
      <MultiContentRow>
        <Dropdown
          attr={messageTypeAttr}
          data-aid="message.type"
          description="Use a recording or enter text to be said aloud for a customer to hear"
          disabled={disabled}
          label="Message"
          onChange={useOnMessageTypeChange}
          options={MESSAGE_TYPES_OPTIONS}
          readOnly={readOnly}
        />

        {isRecordingMessage && (
          <AudioSection url={currentMessage} messageAttr={messageAttr} readOnly={readOnly || disabled} />
        )}
      </MultiContentRow>

      {isTextMessage && (
        <>
          <Row>
            <TextareaField
              attr={messageAttr}
              data-aid="message.message"
              description="Message that will be said to the customer"
              disabled={disabled || readOnly}
              label="Text"
            />
          </Row>

          <VoiceSelector attr={attr} disabled={disabled} readOnly={readOnly} />
        </>
      )}
    </>
  );
};

const AudioSection: FC<AudioSectionType> = memo(props => {
  const { url: urlFromConfig = '', readOnly, messageAttr } = props;
  const { onBlur, updateState, workingState } = useContext(FormContext);
  const [trackLabel, setTrackLabel] = useState<string>();
  const [isRecordingLibraryVisible, setRecordingLibraryVisible] = useState<boolean>(false);
  const isRecordingAvailable = !!urlFromConfig;

  // If a record appears in a node and is a part of a template it may be replaced in the Recording library
  // Adding 'timestamp' to the recording URL handles the case when browser uses the cached version of the replaced audio
  const [timeStamp] = useState<number | null>(readOnly || !isRecordingAvailable ? null : new Date().getTime());
  const recordingUrl = `${urlFromConfig}${timeStamp ? `?timestamp=${timeStamp}` : ''}`;

  const fileNameFromUrl = getRecordingFileName(urlFromConfig);
  const fileNameWithTemplate = fileNameFromUrl.match(FILENAME_WITH_TEMPLATE_REGEX);

  useEffect(() => setTrackLabel(fileNameFromUrl), [fileNameFromUrl]);

  const playerSection =
    fileNameWithTemplate || !isRecordingAvailable ? null : (
      <AudioPlayer
        attr={messageAttr}
        data-aid={`${messageAttr}-player`}
        filled
        trackLabel={trackLabel}
        value={recordingUrl}
      />
    );

  const onAudioSelected = useCallback(
    newRecordingUrl => {
      updateState(set({}, messageAttr, newRecordingUrl));
      onBlur &&
        onBlur({
          currentState: produce(workingState, draftState => set(draftState, messageAttr, newRecordingUrl)),
        });

      const fileName = getRecordingFileName(newRecordingUrl);
      setTrackLabel(fileName);
      setRecordingLibraryVisible(false);
    },
    [messageAttr, onBlur, updateState, workingState]
  );

  const onActionButtonPress = () => {
    setRecordingLibraryVisible(true);
    analytics.track('Admin - Recording Library - Opened');
  };

  const actions = (
    <RecordingActions>
      <Button data-aid={`${messageAttr}-action`} buttonType={ButtonTypes.SECONDARY} onClick={onActionButtonPress}>
        {isRecordingAvailable || fileNameWithTemplate ? 'Update' : 'Select'} recording
      </Button>
    </RecordingActions>
  );

  return (
    <>
      {isRecordingLibraryVisible && (
        <RecordingLibrary
          currentRecordingUrl={urlFromConfig}
          onCancel={() => setRecordingLibraryVisible(false)}
          onSave={onAudioSelected}
        />
      )}
      <AudioSectionContainer data-aid={messageAttr}>
        <SectionHeader data-aid={`${messageAttr}-title`}>Recording</SectionHeader>
        <SectionDescription data-aid={`${messageAttr}-description`}>
          Select from a list of recordings or upload a new one.
        </SectionDescription>
        {playerSection}
        {actions}
      </AudioSectionContainer>
    </>
  );
});

const RecordingActions = styled.div`
  align-items: center;
  display: flex;
  padding-top: 4px;
`;

const AudioSectionContainer = styled.div`
  padding-top: 8px;
`;

export const SectionHeader = styled.div`
  align-self: flex-start;
  font-weight: bolder;
  margin-bottom: ${p => p.theme.spacing.stackSmall};
`;

const SectionDescription = styled.div`
  align-self: flex-start;
  font-size: ${p => p.theme.fontSize.base};
  color: ${p => p.theme.colors.gray700};
  line-height: 1.6;
  margin-bottom: ${p => p.theme.spacing.stackSmall};
  min-height: 18px;
`;

export default MessageTypeSelector;
